import React, { useState, useEffect } from "react";
import Form from "../../Form";
import { Drawer } from "antd";
import styles from "./TicketProcessingFilter.module.scss";
import {
  filterFieldConstants,
  filterFormMap,
  filterHeaderStyle,
  FilterOptionFields,
  ticketFilterConstants,
} from "./constants";
import TNButton from "../../../reusable-components/TNButton";
import { ButtonShape, ButtonSize } from "../../../enums/ButtonAttributesEnums";
import InputField from "../../../reusable-components/TNInputField";
import { useTicketProcessingFilterContext } from "../../../context/TicketProcessingFilterContext";
import TicketProcessingServices from "../../../services/ticketProcessing.service";

export interface TicketViewFilterProps {
  isOpen?: boolean;
  handleClose?: () => void;
}

const TicketProcessingFilter = (props: TicketViewFilterProps) => {
  const { isOpen, handleClose } = props;

  const { CUSTOMER_FILTER_NAME, TRUCK_FILTER_NAME, JOB_NAME_FILTER_NAME } =
    filterFieldConstants;

  const { processingFilters, updateProcessingFilters, resetProcessingFilters } =
    useTicketProcessingFilterContext();

  const [formKey, setFormKey] = useState(0);

  const { FetchTicketProcessingFilterMeta } = TicketProcessingServices();
  const { data } = FetchTicketProcessingFilterMeta();

  const customerOptions = data?.customers?.map((item) => {
    return {
      label: item?.companyName,
      value: String(item?.companyId),
      key: String(item?.companyId),
    };
  });

  const truckOptions = data?.trucks?.map((item) => {
    return {
      label: item?.truckId,
      value: String(item?.truckId),
      key: String(item?.truckId),
    };
  });

  const jobNameOptions = data?.jobNames?.map((item) => {
    return {
      label: item?.jobName,
      value: String(item?.jobId),
      key: String(item?.jobId),
    };
  });

  const getOptionsList = (fieldName: string) => {
    switch (fieldName) {
      case CUSTOMER_FILTER_NAME:
        return customerOptions;
      case TRUCK_FILTER_NAME:
        return truckOptions;
      case JOB_NAME_FILTER_NAME:
        return jobNameOptions;
    }
  };

  const getDefaultFilterValues = () => {
    return processingFilters?.customerName ||
      processingFilters?.truckNumber ||
      processingFilters?.jobName
      ? {
          [CUSTOMER_FILTER_NAME]: processingFilters?.customerName,
          [TRUCK_FILTER_NAME]: processingFilters?.truckNumber,
          [JOB_NAME_FILTER_NAME]: processingFilters?.jobName,
        }
      : {};
  };

  return (
    <>
      <Form key={formKey} defaultValues={getDefaultFilterValues()}>
        {({ watch, getValues, setValue, resetField }) => {
          const clearFormFields = () => {
            resetField(CUSTOMER_FILTER_NAME);
            resetField(JOB_NAME_FILTER_NAME);
            resetField(TRUCK_FILTER_NAME);
          };

          const handleFilterPersistance = () => {
            filterFormMap.forEach(({ key, field }) => {
              const filterValue = processingFilters[key];
              const formValue = getValues(field);
              if (!!filterValue && !!!formValue) {
                setValue(field, filterValue);
              } else if (!!!filterValue && !!formValue) {
                setValue(field, undefined);
              } else if (
                !!filterValue &&
                !!formValue &&
                filterValue !== formValue
              ) {
                setValue(field, filterValue);
              }
            });
          };

          const closeFilterDrawer = () => {
            handleFilterPersistance();
            handleClose();
          };

          const closeFilterDrawerAfterClear = () => {
            handleClose();
          };

          const DrawerHeader = (
            <div className={styles.DrawerHeaderContainer}>
              <p>{ticketFilterConstants.HEADER_TXT}</p>
            </div>
          );

          const DrawerFooter = (
            <div className={styles.DrawerFooterContainer}>
              <TNButton
                className={ticketFilterConstants.CANCEL_BTN_CLASS}
                shape={ButtonShape.DEFAULT}
                size={ButtonSize.LARGE}
                text={ticketFilterConstants.CANCEL_BTN_TXT}
                onClick={closeFilterDrawer}
              />
              <TNButton
                shape={ButtonShape.DEFAULT}
                size={ButtonSize.LARGE}
                text={ticketFilterConstants.SUBMIT_BTN_TXT}
                onClick={() => {
                  updateProcessingFilters({
                    ...processingFilters,
                    customerName: getValues(CUSTOMER_FILTER_NAME),
                    truckNumber: getValues(TRUCK_FILTER_NAME),
                    jobName: getValues(JOB_NAME_FILTER_NAME),
                  });
                  handleClose();
                }}
              />
            </div>
          );

          return (
            <Drawer
              visible={isOpen}
              maskClosable={false}
              destroyOnClose
              mask={true}
              closable={true}
              getContainer={false}
              placement={ticketFilterConstants.DRAWER_PLACEMENT}
              size={ticketFilterConstants.DRAWER_SIZE}
              title={DrawerHeader}
              footer={DrawerFooter}
              headerStyle={filterHeaderStyle}
              className={styles.ticketViewFilters}
              height={ticketFilterConstants.DRAWER_HEIGHT}
              width={ticketFilterConstants.DRAWER_WIDTH}
              onClose={closeFilterDrawer}
            >
              <>
                <div className={styles.clearFilterWrapper}>
                  <span
                    className={styles.clearTextContainer}
                    onClick={() => {
                      resetProcessingFilters();
                      setFormKey((prev) => prev + 1);
                      closeFilterDrawerAfterClear();
                    }}
                  >
                    {ticketFilterConstants.CLEAR_BTN_TXT}
                  </span>
                </div>
                <div className={styles.filterFieldsWrapper}>
                  {FilterOptionFields.map((field) => {
                    return (
                      <InputField
                        type={field.type}
                        name={field.name}
                        label={field.label}
                        bordered={field.bordered}
                        placeholder={field.placeholder}
                        showSearch={field.showSearch}
                        optionFilterProp={field.optionFilterProp}
                        disabledDates={field.disabledDates}
                        options={getOptionsList(field.name)}
                        allowClear={field.allowClear}
                      />
                    );
                  })}
                </div>
              </>
            </Drawer>
          );
        }}
      </Form>
    </>
  );
};

export default TicketProcessingFilter;
